import { api } from "./client";

export interface InvoiceCustomerPayload {
  eik: string;
  vat_number?: string | null;
  company_name: string;
  city: string;
  address: string;
  mol?: string | null;
}

export interface InvoiceLinePayload {
  name: string;
  unit: string;
  vat_rate: number;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface InvoiceCreatePayload {
  invoice_number: string;
  invoice_type: "tax" | "simplified";
  issue_date: string; // YYYY-MM-DD
  supply_date: string;
  supply_place?: string | null;
  payment_method_id?: number | null;
  customer: InvoiceCustomerPayload;
  received_by?: string | null;
  received_at?: string | null;
  note?: string | null;
  bill_ids: number[];
  lines: InvoiceLinePayload[];
}

export interface InvoiceLine {
  id: number;
  sort_index: number;
  name: string;
  unit: string;
  vat_rate: number;
  quantity: number;
  unit_price: number;
  total: number;
}

export interface Invoice {
  id: number;
  invoice_number: string;
  invoice_type: string;
  issue_date: string;
  supply_date: string;
  supply_place: string | null;
  payment_method_id: number | null;

  company_id: number;
  supplier_name: string;
  supplier_eik: string;
  supplier_vat: string | null;
  supplier_address: string | null;
  supplier_mol: string | null;
  supplier_bank_name: string | null;
  supplier_bic: string | null;
  supplier_iban: string | null;

  customer_id: number;
  customer_name: string;
  customer_eik: string;
  customer_vat: string | null;
  customer_city: string;
  customer_address: string;
  customer_mol: string | null;

  received_by: string | null;
  received_at: string | null;
  note: string | null;

  subtotal_taxable: number;
  subtotal_non_taxable: number;
  vat_total: number;
  total_sum: number;
  grand_total: number;

  created_at: string;
  created_by_user_id: number;
  created_by_name: string | null;

  cancelled_at: string | null;
  cancelled_by_user_id: number | null;

  lines: InvoiceLine[];
  bill_ids: number[];
}

export const createInvoice = async (
  data: InvoiceCreatePayload,
): Promise<Invoice> => {
  const response = await api.post<Invoice>("/invoices", data);
  return response.data;
};

export interface InvoiceListFilters {
  date_from?: string; // YYYY-MM-DD
  date_to?: string;
  customer_id?: number;
  invoice_type?: "tax" | "simplified";
  q?: string;
}

export const listInvoices = async (
  filters?: InvoiceListFilters,
): Promise<Invoice[]> => {
  const params = new URLSearchParams();
  if (filters?.date_from) params.append("date_from", filters.date_from);
  if (filters?.date_to) params.append("date_to", filters.date_to);
  if (filters?.customer_id != null)
    params.append("customer_id", String(filters.customer_id));
  if (filters?.invoice_type)
    params.append("invoice_type", filters.invoice_type);
  if (filters?.q && filters.q.trim()) params.append("q", filters.q.trim());
  const qs = params.toString();
  const response = await api.get<Invoice[]>(
    qs ? `/invoices?${qs}` : "/invoices",
  );
  return response.data;
};

export const getInvoiceById = async (id: number): Promise<Invoice> => {
  const response = await api.get<Invoice>(`/invoices/${id}`);
  return response.data;
};

export const cancelInvoice = async (id: number): Promise<Invoice> => {
  const response = await api.post<Invoice>(`/invoices/${id}/cancel`);
  return response.data;
};

/**
 * Коригира получателя на издадена фактура — същите полета и валидации като
 * при издаване (BE `InvoiceCustomerIn`). Пипа само снимката върху самия
 * запис (`customer_*`), не регистъра на клиентите; суми, редове, номер и
 * дати не се променят. Анулирана фактура се отказва с 409.
 */
export const updateInvoiceCustomer = async (
  id: number,
  data: InvoiceCustomerPayload,
): Promise<Invoice> => {
  const response = await api.patch<Invoice>(`/invoices/${id}/customer`, data);
  return response.data;
};

export interface PrintInvoiceResult {
  status: "success" | string;
  result?: Record<string, unknown> | null;
}

/**
 * Печат на вече издадена фактура на POS (ESC/POS) принтера на оператора.
 *
 * `duplicate` — повторен печат от справките: излиза едно копие с етикет
 * „ДУБЛИКАТ", а не втори оригинал (оригиналът вече е у клиента).
 */
export const printInvoicePos = async (
  id: number,
  options?: { duplicate?: boolean },
): Promise<PrintInvoiceResult> => {
  const qs = options?.duplicate ? "?duplicate=true" : "";
  const response = await api.post<PrintInvoiceResult>(
    `/invoices/${id}/print${qs}`,
  );
  return response.data;
};
